/**
 * Per-user AI daily briefing API client (2026-07-21).
 *
 * Powers the "Your daily briefing" card on the agent dashboard. The server
 * generates on demand (GET) or overwrites (POST regenerate). Focus 'none'
 * means no briefing for this user and the card self-hides.
 */
import { apiFetch } from '../auth/api';

export type BriefingTone = 'normal' | 'urgent' | 'good';
export type BriefingFocus = 'agent' | 'operations' | 'tc' | 'listing_marketing' | 'none';

export interface BriefingItem {
  text: string;
  tone: BriefingTone;
}
export interface BriefingSection {
  label: string;
  items: BriefingItem[];
}

/** Shape when the caller has a real briefing for today. */
export interface BriefingActive {
  date: string;
  focus: Exclude<BriefingFocus, 'none'>;
  headline: string;
  sections: BriefingSection[];
  summary: string | null;
  generated_at: number;
}
/** Shape when there is nothing to show (card hides). */
export interface BriefingNone {
  date?: string;
  focus: 'none';
}

export type BriefingResponse = BriefingActive | BriefingNone;

export function isActiveBriefing(b: BriefingResponse | undefined): b is BriefingActive {
  return !!b && b.focus !== 'none';
}

export function getTodayBriefing(): Promise<BriefingResponse> {
  return apiFetch('/api/briefing/today');
}

export function regenerateBriefing(): Promise<BriefingResponse> {
  return apiFetch('/api/briefing/regenerate', { method: 'POST' });
}

// ---------------------------------------------------------------------
// Team-settings side: per-member briefing focus (Team page).
// ---------------------------------------------------------------------

export interface BriefingFocusMember {
  user_id: number;
  first_name: string;
  last_name: string | null;
  role: string;
  /** null = derive from role. */
  briefing_focus: BriefingFocus | null;
  /** Role-derived (or explicit) resolved focus, for showing the default. */
  resolved_focus: BriefingFocus;
  /** Whether the member gets the briefing emailed each morning. */
  email_enabled: boolean;
}

export function getBriefingFocuses(): Promise<{ members: BriefingFocusMember[] }> {
  return apiFetch('/api/team/briefing-focus');
}

export function setBriefingFocus(userId: number, focus: BriefingFocus | null): Promise<{ ok: true }> {
  return apiFetch('/api/team/briefing-focus', {
    method: 'PATCH',
    body: JSON.stringify({ user_id: userId, briefing_focus: focus }),
  });
}

export function setBriefingEmail(userId: number, enabled: boolean): Promise<{ ok: true }> {
  return apiFetch('/api/team/briefing-focus', {
    method: 'PATCH',
    body: JSON.stringify({ user_id: userId, email_enabled: enabled }),
  });
}
